import React, { useState, useMemo } from 'react';
import DateField from '../Shared/DateField';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Tarife OSIM opozitii (LEI, 01.01.2026) ── */
const TAXE_DEFAULT = {
  opozitie_clasa:        305,   // Anexa 4, pct. 13, prima clasa
  opozitie_clasa_supl:   152,   // Anexa 4, pct. 13, fiecare clasa suplimentara
  termen_suplimentar:     51,   // pct. 14, câte 3 luni
  divizare:              254,   // pct. 15
  contestatie_opozitie: 1270,   // pct. 17 lit. b
  contestatie_decizie:   762,   // pct. 17 lit. a
};

const TERMENE_DEFAULT = {
  opozitie_luni:    2,   // de la publicarea cererii
  raspuns_zile:    30,   // punctul de vedere al solicitantului
  conciliere_luni:  2,   // perioadă de negociere, la cererea partilor
  dovada_luni:      2,   // dovada folosirii efective
  contestatie_zile: 30,  // de la comunicarea deciziei
};

const TEMEIURI = [
  ['identitate',       'Identitate de semne si de produse — art. 6 alin. (1) lit. a)'],
  ['risc_confuzie',    'Risc de confuzie / asociere — art. 6 alin. (1) lit. b)'],
  ['renume',           'Marca de renume — profit necuvenit sau prejudiciu'],
  ['notorie',          'Marca notorie neînregistrata'],
  ['agent',            'Cerere depusa de agent fara consimtamant'],
  ['anterior_neinreg', 'Drept anterior neînregistrat (nume comercial, drept de autor, desen)'],
  ['ig',               'Indicatie geografica sau denumire de origine'],
];

const TIP_DREPT = [
  ['ro',     'Marca nationala (OSIM)'],
  ['eu',     'Marca a Uniunii Europene (EUIPO)'],
  ['int',    'Inregistrare internationala cu efect in Romania'],
  ['notorie','Marca notorie'],
  ['alt',    'Alt drept anterior'],
];

const SOLUTII = [
  ['',              '— nesolutionata —'],
  ['respinsa',      'Opozitie respinsa — cererea continua'],
  ['admisa_partial','Opozitie admisa partial — cerere limitata'],
  ['admisa_total',  'Opozitie admisa in totalitate — cerere respinsa'],
  ['retrasa',       'Opozitie retrasa'],
  ['acord',         'Stinsa prin acord de coexistenta'],
  ['suspendata',    'Procedura suspendata'],
];

const STRATEGII = [
  ['',          '— nestabilita —'],
  ['aparare',   'Aparare pe fond'],
  ['dovada',    'Solicitare dovada a folosirii'],
  ['limitare',  'Limitarea listei de produse/servicii'],
  ['divizare',  'Divizarea cererii'],
  ['negociere', 'Negociere / acord de coexistenta'],
  ['retragere', 'Retragerea cererii'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtLei = (n) => Number(n).toLocaleString('ro-RO') + ' lei';

export function emptyRecord() {
  return {
    directie: 'primita',     // primita | formulata
    marca: '',
    numarDepozit: '',
    clientul: '',
    claseMarca: '',
    dataPublicare: '',
    numarOpozitie: '',
    dataOpozitie: '',
    dataNotificare: '',
    oponent: '',
    mandatarOponent: '',
    tipDrept: 'ro',
    marcaOpusa: '',
    numarMarcaOpusa: '',
    dataMarcaOpusa: '',
    claseOpuse: '',
    temeiuri: [],
    raspunsDepus: false,
    dataRaspuns: '',
    conciliere: false,
    dataConciliereStart: '',
    dovadaCeruta: false,
    dataCerereDovada: '',
    dovadaPrimita: false,
    termenSuplimentar: 0,
    strategie: '',
    solutie: '',
    dataDecizie: '',
    contestatie: false,
    dataContestatie: '',
    transmisClient: false,
    observatii: '',
  };
}

/* ── Fee calc ── */
function calcTaxe(r, T) {
  const nClase = Math.max(1, (r.claseOpuse || '').split(/[,;\s]+/).filter(Boolean).length || 1);
  const opozitie = T.opozitie_clasa + (nClase - 1) * T.opozitie_clasa_supl;
  const termene = (Number(r.termenSuplimentar) || 0) * T.termen_suplimentar;
  const contestatie = r.contestatie ? T.contestatie_opozitie : 0;
  const divizare = r.strategie === 'divizare' ? T.divizare : 0;
  return { nClase, opozitie, termene, contestatie, divizare, totalNoi: termene + contestatie + divizare };
}

/* ── Status / timeline ── */
function deriveStatus(r, TM) {
  const pub = parseISO(r.dataPublicare);
  const op = parseISO(r.dataOpozitie);
  const notif = parseISO(r.dataNotificare);
  const conc = parseISO(r.dataConciliereStart);
  const cerDov = parseISO(r.dataCerereDovada);
  const dec = parseISO(r.dataDecizie);

  const termenOpozitie = pub ? addMonths(pub, TM.opozitie_luni) : null;
  const termenRaspunsBaza = notif ? addDays(notif, TM.raspuns_zile) : null;
  const termenRaspuns = termenRaspunsBaza
    ? addMonths(termenRaspunsBaza, 3 * (Number(r.termenSuplimentar) || 0))
    : null;
  const termenConciliere = r.conciliere && conc ? addMonths(conc, TM.conciliere_luni) : null;
  const termenDovada = r.dovadaCeruta && cerDov ? addMonths(cerDov, TM.dovada_luni) : null;
  const termenContestatie = dec ? addDays(dec, TM.contestatie_zile) : null;

  const steps = [
    { key:'publicare',   label:'Publicare cerere',      done: !!pub,           date: pub },
    { key:'opozitie',    label:'Opozitie inregistrata', done: !!op,            date: op },
    { key:'notificare',  label:'Notificare OSIM',       done: !!notif,         date: notif },
    { key:'raspuns',     label:'Punct de vedere',       done: r.raspunsDepus,  date: termenRaspuns },
    { key:'dovada',      label:'Dovada folosirii',      done: !r.dovadaCeruta || r.dovadaPrimita, date: termenDovada },
    { key:'decizie',     label:'Decizie comisie',       done: !!dec,           date: dec },
    { key:'contestatie', label:'Termen contestatie',    done: !!dec && (r.contestatie || (termenContestatie ? todayMid() > termenContestatie : false)), date: termenContestatie },
    { key:'client',      label:'Informat clientul',     done: r.transmisClient, date: null },
  ];

  let alert;
  if (!op) {
    if (termenOpozitie && todayMid() < termenOpozitie)
      alert = { label:'Termen de opozitie in curs — monitorizare', date: termenOpozitie, sev:'watch' };
    else
      alert = { label:'Opozitie de inregistrat in dosar', sev:'neutral' };
  } else if (!notif) {
    alert = { label:'Asteptare notificare din partea OSIM', sev:'info' };
  } else if (r.conciliere && termenConciliere && todayMid() < termenConciliere) {
    alert = { label:'Perioada de conciliere intre parti', date: termenConciliere, sev:'watch' };
  } else if (r.dovadaCeruta && !r.dovadaPrimita) {
    alert = { label:'Termen pentru dovada folosirii efective', date: termenDovada, sev:'act' };
  } else if (!r.raspunsDepus) {
    alert = { label:'De depus punctul de vedere la opozitie', date: termenRaspuns, sev:'act' };
  } else if (!dec) {
    alert = { label:'In examinare la comisia de opozitii', sev:'info' };
  } else if (!r.contestatie && termenContestatie && todayMid() <= termenContestatie) {
    alert = { label:'Termen de contestatie impotriva deciziei', date: termenContestatie, sev:'act' };
  } else if (r.contestatie) {
    alert = { label:'Contestatie depusa — in examinare', sev:'info' };
  } else if (!r.transmisClient) {
    alert = { label:'De informat clientul despre solutie', sev:'act' };
  } else {
    alert = { label:'Dosar inchis', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0 && alert.sev === 'act') alert.sev = 'overdue';
  }

  return { steps, alert, termenOpozitie, termenRaspuns, termenConciliere, termenDovada, termenContestatie };
}

/* ════════════════════════ Component ════════════════════════ */
export default function OpozitieOSIMTracker({ data, onChange, showToast }) {
  const [r, setR] = useState(data);
  const [taxe, setTaxe] = useState(TAXE_DEFAULT);
  const [termene, setTermene] = useState(TERMENE_DEFAULT);
  const [showSetari, setShowSetari] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const taxeCalc = useMemo(() => calcTaxe(r, taxe), [r, taxe]);
  const status = useMemo(() => deriveStatus(r, termene), [r, termene]);

  const toggleTemei = (k) => {
    const t = r.temeiuri.includes(k) ? r.temeiuri.filter(x => x !== k) : [...r.temeiuri, k];
    set({ temeiuri: t });
  };

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea dosarului">
        <div className="field">
          <label>Directia opozitiei</label>
          <SegControl value={r.directie} onChange={v => set({ directie: v })}
            options={[['primita','Opozitie primita (clientul e solicitant)'],['formulata','Formulam noi opozitia']]} />
        </div>
        <div className="f-row">
          <div className="field">
            <label>Denumirea marcii</label>
            <input type="text" value={r.marca} onChange={e => set({ marca: e.target.value })} placeholder="ex: Aurora Style" />
          </div>
          <div className="field">
            <label>Nr. depozit OSIM</label>
            <input type="text" value={r.numarDepozit} onChange={e => set({ numarDepozit: e.target.value })} placeholder="M 2026 01234" className="mono" />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Clientul</label>
            <input type="text" value={r.clientul} onChange={e => set({ clientul: e.target.value })} />
          </div>
          <div className="field">
            <label>Clase Nisa ale marcii</label>
            <input type="text" value={r.claseMarca} onChange={e => set({ claseMarca: e.target.value })} placeholder="ex: 3, 5, 44" className="mono" />
          </div>
        </div>
        <div className="field">
          <label>Data publicarii cererii in BOPI</label>
          <DateField value={r.dataPublicare} onChange={v => set({ dataPublicare: v })} />
          {status.termenOpozitie && (
            <span className="f-hint">Termen opozitie: {fmtRO(status.termenOpozitie)} ({daysLeft(status.termenOpozitie)} zile)</span>
          )}
        </div>
      </TrackerCard>

      {/* Opozitia */}
      <TrackerCard title="Opozitia">
        <div className="f-row">
          <div className="field">
            <label>Nr. opozitie</label>
            <input type="text" value={r.numarOpozitie} onChange={e => set({ numarOpozitie: e.target.value })} className="mono" />
          </div>
          <div className="field">
            <label>Data depunerii opozitiei</label>
            <DateField value={r.dataOpozitie} onChange={v => set({ dataOpozitie: v })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Oponent</label>
            <input type="text" value={r.oponent} onChange={e => set({ oponent: e.target.value })} />
          </div>
          <div className="field">
            <label>Mandatar oponent</label>
            <input type="text" value={r.mandatarOponent} onChange={e => set({ mandatarOponent: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label>Data notificarii OSIM</label>
          <DateField value={r.dataNotificare} onChange={v => set({ dataNotificare: v })} />
          <span className="f-hint">De la aceasta data curge termenul de raspuns.</span>
        </div>

        <div className="tr-divider">Dreptul anterior invocat</div>
        <div className="f-row">
          <div className="field">
            <label>Tip drept anterior</label>
            <FilterPill value={r.tipDrept} onChange={e => set({ tipDrept: e.target.value })}>
              {TIP_DREPT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Marca / dreptul opus</label>
            <input type="text" value={r.marcaOpusa} onChange={e => set({ marcaOpusa: e.target.value })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Nr. marca opusa</label>
            <input type="text" value={r.numarMarcaOpusa} onChange={e => set({ numarMarcaOpusa: e.target.value })} className="mono" />
          </div>
          <div className="field">
            <label>Data marcii opuse</label>
            <DateField value={r.dataMarcaOpusa} onChange={v => set({ dataMarcaOpusa: v })} />
          </div>
        </div>
        <div className="field">
          <label>Clase opuse</label>
          <input type="text" value={r.claseOpuse} onChange={e => set({ claseOpuse: e.target.value })} placeholder="ex: 3, 5" className="mono" />
          <span className="f-hint">Clasele vizate de opozitie — determina calculul taxei.</span>
        </div>

        <div className="tr-divider">Temeiuri legale invocate</div>
        <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
          {TEMEIURI.map(([k, l]) => (
            <label key={k} className="check-label">
              <input type="checkbox" checked={r.temeiuri.includes(k)} onChange={() => toggleTemei(k)} />
              {l}
            </label>
          ))}
        </div>
      </TrackerCard>

      {/* Desfasurare */}
      <TrackerCard title="Desfasurarea procedurii">
        {status.termenRaspuns && !r.raspunsDepus && (
          <DeadlineChip label={`Termen punct de vedere (${termene.raspuns_zile} zile + ${r.termenSuplimentar}×3 luni)`} date={status.termenRaspuns} act />
        )}
        <div className="f-row" style={{ marginTop: 8 }}>
          <div className="field">
            <label>Termene suplimentare solicitate</label>
            <input type="number" min="0" max="4" value={r.termenSuplimentar}
              onChange={e => set({ termenSuplimentar: Math.max(0, Number(e.target.value) || 0) })} />
            <span className="f-hint">Fiecare termen adauga 3 luni si {taxe.termen_suplimentar} lei</span>
          </div>
          <div className="field">
            <label>Strategie</label>
            <FilterPill value={r.strategie} onChange={e => set({ strategie: e.target.value })}>
              {STRATEGII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.raspunsDepus} onChange={e => set({ raspunsDepus: e.target.checked })} />
          Punctul de vedere a fost depus
        </label>
        {r.raspunsDepus && (
          <div className="field" style={{ marginLeft: 22 }}>
            <label>Data depunerii raspunsului</label>
            <DateField value={r.dataRaspuns} onChange={v => set({ dataRaspuns: v })} />
          </div>
        )}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.conciliere} onChange={e => set({ conciliere: e.target.checked })} />
          Perioada de conciliere intre parti
        </label>
        {r.conciliere && (
          <div className="dep-section">
            <div className="field">
              <label>Data inceperii concilierii</label>
              <DateField value={r.dataConciliereStart} onChange={v => set({ dataConciliereStart: v })} />
            </div>
            {status.termenConciliere && (
              <DeadlineChip label={`Sfarsit conciliere (${termene.conciliere_luni} luni)`} date={status.termenConciliere} />
            )}
          </div>
        )}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.dovadaCeruta} onChange={e => set({ dovadaCeruta: e.target.checked })} />
          S-a solicitat dovada folosirii efective a marcii opuse
        </label>
        {r.dovadaCeruta && (
          <div className="dep-section">
            <div className="field">
              <label>Data cererii de dovada</label>
              <DateField value={r.dataCerereDovada} onChange={v => set({ dataCerereDovada: v })} />
            </div>
            {status.termenDovada && (
              <DeadlineChip label={`Termen dovada (${termene.dovada_luni} luni)`} date={status.termenDovada} act={!r.dovadaPrimita} />
            )}
            <label className="check-label">
              <input type="checkbox" checked={r.dovadaPrimita} onChange={e => set({ dovadaPrimita: e.target.checked })} />
              Dovada a fost primita si analizata
            </label>
          </div>
        )}

        <div className="tr-divider">Decizie</div>
        <div className="f-row">
          <div className="field">
            <label>Solutia comisiei</label>
            <FilterPill value={r.solutie} onChange={e => set({ solutie: e.target.value })}>
              {SOLUTII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Data deciziei</label>
            <DateField value={r.dataDecizie} onChange={v => set({ dataDecizie: v })} />
          </div>
        </div>
        {status.termenContestatie && (
          <DeadlineChip label={`Termen contestatie (${termene.contestatie_zile} zile)`} date={status.termenContestatie} act={!r.contestatie} />
        )}
        <label className="check-label mt8">
          <input type="checkbox" checked={r.contestatie} onChange={e => set({ contestatie: e.target.checked })} />
          Decizia a fost contestata
        </label>
        {r.contestatie && (
          <div className="field" style={{ marginLeft: 22 }}>
            <label>Data contestatiei</label>
            <DateField value={r.dataContestatie} onChange={v => set({ dataContestatie: v })} />
          </div>
        )}
        <label className={`check-label mt8${!r.solutie ? ' disabled' : ''}`}>
          <input type="checkbox" disabled={!r.solutie} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
          Clientul a fost informat despre solutie
        </label>
      </TrackerCard>

      {/* Calcul taxe */}
      <TrackerCard title="Calcul taxe OSIM" badge="tarife 01.01.2026">
        <table className="fee-table">
          <tbody>
            <FeeRow label={`Taxa opozitie (${taxeCalc.nClase} clas${taxeCalc.nClase > 1 ? 'e' : 'a'})`} value={taxeCalc.opozitie} />
            {taxeCalc.nClase > 1 && (
              <FeeRow label={`  din care: clasa principala ${taxe.opozitie_clasa} + ${taxeCalc.nClase-1} × ${taxe.opozitie_clasa_supl} lei`} value={0} muted />
            )}
            {taxeCalc.termene > 0 && <FeeRow label={`Termene suplimentare (${r.termenSuplimentar} × ${taxe.termen_suplimentar} lei)`} value={taxeCalc.termene} />}
            {taxeCalc.contestatie > 0 && <FeeRow label="Taxa contestatie la decizie" value={taxeCalc.contestatie} />}
            {taxeCalc.divizare > 0 && <FeeRow label="Taxa divizare cerere" value={taxeCalc.divizare} />}
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Total taxe in sarcina noastra</td>
              <td>{fmtLei(taxeCalc.nClase > 0 ? (r.directie === 'formulata' ? taxeCalc.opozitie : 0) + taxeCalc.termene + taxeCalc.contestatie + taxeCalc.divizare : taxeCalc.totalNoi)}</td>
            </tr>
          </tfoot>
        </table>
        <p className="f-hint mt4">
          {r.directie === 'primita'
            ? `Expunere la taxa adversarului daca admitem: ${fmtLei(taxeCalc.opozitie)}`
            : `Taxa opozitie formulata de noi: ${fmtLei(taxeCalc.opozitie)}`}
        </p>
        <button type="button" className="btn-link mt8" onClick={() => setShowSetari(s => !s)}>
          {showSetari ? '▾' : '▸'} Tarife si termene (editabile)
        </button>
        {showSetari && (
          <div className="tarife-editor">
            <div className="tr-divider">Tarife (lei)</div>
            {[['opozitie_clasa','Prima clasa'],['opozitie_clasa_supl','Clasa supl.'],['termen_suplimentar','Termen supl.'],
              ['divizare','Divizare'],['contestatie_opozitie','Contestatie opozitie'],['contestatie_decizie','Contestatie decizie']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={taxe[k]} onChange={e => setTaxe({ ...taxe, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
            <div className="tr-divider">Termene</div>
            {[['opozitie_luni','Opozitie (luni)'],['raspuns_zile','Raspuns (zile)'],['conciliere_luni','Conciliere (luni)'],
              ['dovada_luni','Dovada (luni)'],['contestatie_zile','Contestatie (zile)']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={termene[k]} onChange={e => setTermene({ ...termene, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
          </div>
        )}
      </TrackerCard>

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })}
          rows={3} placeholder="Motivele opozitiei, argumente, strategie…" />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>{done ? '✓' : i + 1}</span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}>
        <span className="tr-card-title">{title}</span>
        <div className="tr-card-head-right">
          {badge && <span className="tr-badge">{badge}</span>}
          {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
        </div>
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options }) {
  return (
    <div className="seg-ctrl">
      {options.map(([v, label]) => (
        <button key={v} type="button" className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, strong, muted }) {
  return (
    <tr className={strong ? 'fee-strong' : muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{muted ? '' : fmtLei(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  if (!date) return null;
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}
